<template>
  <div>
    <div v-if="productData.loading" class="text-center" style="margin-top: 20%">
      <LayoutContent>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </LayoutContent>
    </div>
    <div v-else>
      <LayoutContent v-if="productData.product.product_category">
        <SectionTitle :label="label" />
        <div v-if="productData.product.product_category_id !== 7">
          <v-row>
            <v-col :xs="12" :sm="5" :cols="12" v-if="defaultProduct">
              <v-carousel class="reposition-navigator" hide-delimiter-background height="unset" show-arrows :show-arrows-on-hover="false" v-if="productData.images.length > 1">
                <v-carousel-item v-for="(productImage, index) in productData.images" :key="index" :ripple="false">
                  <div class="position-relative">
                    <img class="rounded-1r" width="100%" :src="productImage.image" :style="{ opacity: outOfStock ? 0.7 : 1, objectFit: 'cover' }" />
                    <div
                      v-if="defaultProduct.discount_percentage != 0"
                      class="discount-circle font-weight-bold primary lighten-1 d-flex flex-column align-center justify-center white--text rounded-circle"
                    >
                      <span>{{ defaultProduct.discount_percentage }}%</span>
                      <span>{{ $t('label.discountOff') }}</span>
                    </div>
                    <div v-if="outOfStock" class="sold-out-label pa-2 font-weight-bold primary lighten-1 text-center white--text rounded-pill">
                      <span>{{ $t('label.soldOut') }}</span>
                    </div>
                  </div>
                </v-carousel-item>
              </v-carousel>
              <div v-else class="position-relative">
                <img @click="viewImage(productData.product.image)" class="rounded-1r" width="100%" :src="productData.product.image" :style="{ opacity: outOfStock ? 0.7 : 1, objectFit: 'cover' }" />
                <div
                  v-if="defaultProduct.discount_percentage != 0"
                  class="discount-circle font-weight-bold primary lighten-1 d-flex flex-column align-center justify-center white--text rounded-circle"
                >
                  <span>{{ defaultProduct.discount_percentage }}%</span>
                  <span>{{ $t('label.discountOff') }}</span>
                </div>
                <div v-if="outOfStock" class="sold-out-label pa-2 font-weight-bold primary lighten-1 text-center white--text rounded-pill">
                  <span>{{ $t('label.soldOut') }}</span>
                </div>
              </div>
            </v-col>
            <v-col :xs="12" :sm="7" :cols="12" v-if="defaultProduct">
              <div class="ml-lg-4">
                <p class="font-weight-medium grey--text text--lighten-1 mb-1" style="line-height: 120%">{{ productData.product.brand_name }}</p>
                <p class="text-subtitle-1 text-md-h5 font-weight-bold secondary--text mb-4 mb-md-8 text-wrap" style="line-height: 120%">{{ productData.product.product_name }}</p>
                <!-- Product Price -->
                <div>
                  <div class="d-flex justify-space-between">
                    <div style="flex: 1; max-width: 50%" class="d-flex flex-column">
                      <p class="font-weight-medium grey--text text--darken-2">{{ $t('label.retailPrice') }}</p>
                      <span
                        class="font-weight-bold grey--text text--darken-2 text-h4 text-md-h3"
                        :class="{
                          'text-decoration-line-through': isDiscounted
                        }"
                      >
                        {{ `${productData.product.currency} ${formatPrice(defaultProduct.unit_price)}` }}
                      </span>
                      <span v-if="isDiscounted" class="grey--text text--darken-2 font-weight-bold ml-2 text-h5 text-md-h4">
                        {{ `${productData.product.currency} ${formatPrice(defaultProduct.discounted_price)}` }}
                      </span>
                    </div>
                    <div v-if="defaultProduct.agent_price" style="flex: 1">
                      <p class="font-weight-medium orange--text">{{ $t('label.partnerPrice') }}</p>
                      <span class="orange--text font-weight-bold text-h4 text-md-h3">
                        {{ `${productData.product.currency} ${formatPrice(defaultProduct.agent_price)}` }}
                      </span>
                    </div>
                  </div>
                </div>
                <!-- Product Variants -->
                <div class="d-flex flex-row w-full justify-space-between align-center my-5" v-if="productData.product.volume_variation === 1" style="max-width: 750px">
                  <v-btn
                    icon
                    color="grey darken-2"
                    @click="showPrev"
                    class="transparent"
                    :disabled="this.productData.product.volume_variations.findIndex((f) => f.id === this.defaultProduct.id) === 0 ? true : false"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <div class="carousel-container">
                    <VueSlickCarousel ref="carousel" :arrows="false" :slidesToShow="$vuetify.breakpoint.smAndUp ? ($vuetify.breakpoint.lgAndUp ? 4 : 3) : 3" :infinite="false">
                      <div v-for="variation in productData.product.volume_variations" :key="variation.id">
                        <div class="rounded-lg d-flex flex-column justify-center align-center ma-2">
                          <div
                            class="text-center variation-box w-full grey lighten-2 rounded-lg"
                            :class="{
                              'primary lighten-1': defaultProduct.id === variation.id && variation.stock_status !== 'Out of Stock',
                              'grey lighten-1': variation.stock_status === 'Out of Stock' && defaultProduct.id === variation.id
                            }"
                          >
                            <div
                              class="variation-img rounded-t-lg d-flex flex-column justify-center align-center"
                              :class="{
                                'variation-out-of-stock': variation.stock_status === 'Out of Stock' && defaultProduct.id === variation.id,
                                'variation-active': defaultProduct.id === variation.id && variation.stock_status !== 'Out of Stock'
                              }"
                              :style="{ backgroundImage: `url(${variation.image})` }"
                              @click.prevent="setVariation(variation)"
                            >
                              <span class="enlarge-icon" @click="viewImage(variation.image)">
                                <v-icon small color="primary" class="transparent">$enlarge</v-icon>
                              </span>
                            </div>
                            <div :class="{ 'white--text': defaultProduct.id === variation.id }">{{ variation.name }}</div>
                          </div>
                        </div>
                      </div>
                    </VueSlickCarousel>
                  </div>
                  <v-btn
                    icon
                    color="grey darken-2"
                    @click="showNext"
                    :disabled="this.productData.product.volume_variations.findIndex((f) => f.id === this.defaultProduct.id) === this.productData.product.volume_variations.length - 1 ? true : false"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
                <!-- Points To Redeem -->
                <div class="my-5">
                  <p class="text-subtitle-1 font-weight-medium grey--text text--darken-2">{{ $t('label.pointsToRedeem') }}</p>
                  <p class="text-h6 font-weight-bold grey--text text--darken-2">{{ defaultProduct.points_to_redeem || $t('label.notAvailable') }}</p>
                </div>
                <!-- Ratings/Reviews -->
                <div class="d-flex pb-5">
                  <div class="d-flex flex-column mr-3">
                    <p class="mb-1 text-center">{{ $t('label.rating') }}</p>
                    <div class="number-box rounded-lg d-flex justify-center align-center white secondary--text text--lighten-1">{{ defaultProduct.rate_number }}</div>
                  </div>
                  <div class="d-flex flex-column mr-3">
                    <p class="mb-1 text-center">{{ $t('label.reviews') }}</p>
                    <div class="number-box rounded-lg d-flex justify-center align-center primary lighten-1 white--text text--lighten-1">{{ productData.product.total_reviews }}</div>
                  </div>
                  <v-btn v-if="isInWishList" @click.native.prevent="removeFromWishlist" fab small class="elevation-0 white mt-auto heart-icon">
                    <v-icon color="primary" class="transparent">$heart_full</v-icon>
                  </v-btn>
                  <v-btn v-else @click.native.prevent="addToWishlist" fab small class="elevation-0 white mt-auto heart-icon">
                    <v-icon color="primary" class="transparent">$heart</v-icon>
                  </v-btn>
                </div>
                <!-- Partner Banner -->
                <div v-if="role === 'Member' && productData.product.product_category_id === 17" class="mb-5" style="width: 70%; min-width: 400px; max-width: 600px">
                  <AgentBanner @join="showPartnerDialog = true" />
                </div>
                <!-- Product Desc -->
                <div class="pb-15">
                  <h3 class="text-title secondary--text font-weight-bold mb-3">{{ $t('label.aboutThisProduct') }}</h3>
                  <p class="text-body-2 mb-5" v-html="productData.product.description"></p>
                  <button type="button" class="font-weight-bold text-subtitle-2 secondary--text text-decoration-underline" @click="showProductDetails = true">
                    {{ $t('label.viewMoreProductDetails') }}
                  </button>
                </div>
              </div>
            </v-col>
          </v-row>
          <Spacer />
          <ProductReviewSection :productId="productId" />
          <Spacer />
          <!-- <SimilarRecommendations /> -->
          <v-dialog content-class="elevation-0" v-model="showProductDetails" max-width="500">
            <ProductDescription @input="showProductDetails = false" :uses="productData.product.uses" :benefits="productData.product.benefits" :ingredients="productData.product.ingredients" />
          </v-dialog>
          <v-dialog content-class="elevation-0" v-model="imageOverlay" max-width="500">
            <ProductImagePreview @input="imageOverlay = false" :imageSrc="selectedImage" />
          </v-dialog>
        </div>
        <div v-else class="secondary--text">
          <v-row class="my-10">
            <v-col :xs="12" :sm="6" :cols="12" class="d-flex justify-space-between align-center align-sm-start">
              <div class="w-100 white d-flex justify-space-between align-center position-relative rounded-1r" :style="{ height: $vuetify.breakpoint.sm ? '50%' : '100%' }">
                <v-img src="@/assets/voucher-detail.png" contain :style="{ opacity: outOfStock ? 0.7 : 1, height: '45%' }" class="white"></v-img>
                <div
                  v-if="productData.product.discount_percentage != 0"
                  class="discount-circle font-weight-bold primary lighten-1 d-flex flex-column align-center justify-center white--text rounded-circle"
                >
                  <span>{{ productData.product.discount_percentage }}%</span>
                  <span>{{ $t('label.discountOff') }}</span>
                </div>
                <div v-if="outOfStock" class="sold-out-label pa-2 font-weight-bold primary lighten-1 text-center white--text rounded-pill">
                  <span>{{ $t('label.soldOut') }}</span>
                </div>
              </div>
            </v-col>
            <v-col :xs="12" :sm="6" :cols="12">
              <v-container class="ml-lg-4">
                <v-row class="text-h5 font-weight-bold mb-6">{{ productData.product.product_name }}</v-row>
                <v-row class="mb-0">
                  <v-col class="d-flex flex-column pa-0">
                    <span class="font-weight-medium grey--text text--darken-2">{{ $t('label.value') }}</span>
                    <span class="font-weight-bold grey--text text--darken-2 text-h4 text-md-h3">{{ `${productData.product.voucher_value}` }}</span>
                  </v-col>
                </v-row>
                <v-row class="mb-6">
                  <v-col class="d-flex flex-column pa-0">
                    <span class="font-weight-medium grey--text text--darken-2">{{ $t('label.retailPrice') }}</span>
                    <div>
                      <span
                        class="font-weight-bold grey--text text--darken-2 text-h4 text-md-h3"
                        :class="{
                          'text-decoration-line-through': isDiscounted
                        }"
                      >
                        {{ `${productData.product.currency} ${formatPrice(productData.product.unit_price)}` }}
                      </span>
                      <span v-if="isDiscounted" class="grey--text text--darken-2 font-weight-bold ml-2 text-h5 text-md-h4" style="font-weight: 500; font-size: 2.8rem; line-height: 100%">
                        {{ `${productData.product.currency} ${formatPrice(productData.product.discounted_price)}` }}
                      </span>
                    </div>
                    <!-- <span class="font-weight-bold grey--text text--darken-2 text-h3">{{ `${productData.product.currency} ${productData.product.final_price}` }}</span> -->
                  </v-col>
                  <v-col v-if="productData.product.agent_price" class="d-flex flex-column pa-0">
                    <div class="d-flex flex-column align-start">
                      <span class="font-weight-medium orange--text text--darken-2">{{ $t('label.partnerPrice') }}</span>
                      <span class="orange--text font-weight-bold text-h4 text-md-h3">{{ `${productData.product.currency} ${formatPrice(productData.product.agent_price)}` }}</span>
                    </div>
                  </v-col>
                </v-row>
                <v-row class="mb-5">
                  <v-col :cols="3" class="pa-0">
                    <div class="font-weight-bold mb-2">{{ $t('label.expireDate') }}</div>
                    <div class="text-body-2">{{ productData.product.voucher_expired_date | shortDate }}</div>
                  </v-col>

                  <!-- Wishlist button -->
                  <v-col>
                    <v-btn
                      v-if="wishlist.length > 0 && wishlist.findIndex((f) => f.id === productData.product.id) > -1"
                      @click.native.prevent="removeFromWishlist"
                      fab
                      small
                      class="elevation-0 white mt-auto heart-icon"
                    >
                      <v-icon color="primary" class="transparent">$heart_full</v-icon>
                    </v-btn>
                    <v-btn v-else @click.native.prevent="addToWishlist" fab small class="elevation-0 white mt-auto heart-icon">
                      <v-icon color="primary" class="transparent">$heart</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row class="mb-5 p-description" v-html="productData.product.description"></v-row>

                <v-row class="mb-10">
                  <div class="font-weight-bold mb-2">
                    {{ $t('label.termsAndConditions') }}
                  </div>
                  <v-container class="text-body-2 px-0 pr-2">
                    <v-row>
                      <v-col :cols="1" class="py-0">1.</v-col>
                      <v-col class="pa-0">{{ $t('label.voucherTerm1') }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="1" class="py-0">2.</v-col>
                      <v-col class="pa-0">{{ $t('label.voucherTerm2') }}</v-col>
                    </v-row>
                    <v-row>
                      <v-col :cols="1" class="py-0">3.</v-col>
                      <v-col class="pa-0">
                        {{ $t('label.voucherTerm3') }}
                      </v-col>
                    </v-row>
                  </v-container>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </div>
      </LayoutContent>
      <quantity-bar @add-to-cart="addToCartHandler" :quantity="cartQuantity" :min="min" :disabled="this.defaultProduct.stock_status === 'Out of Stock'" />
    </div>
    <WishlistItemAddedToCartDialog v-model="showAddedToCartDialog" :quantity="addedToCartQuantity" @close="resetCartQuantity" />
    <FailedAddToCartDialog v-model="showFailedAddToCartDialog">{{ failedAddToCartMessage }}</FailedAddToCartDialog>
    <v-dialog content-class="elevation-0" v-model="showJoinNowDialog" max-width="500">
      <v-card class="transparent elevation-0">
        <v-card-text class="pa-8 white elevation-3 rounded-lg">
          <p class="font-weight-bold text-subtitle-1">{{ $t('message.partnerOnlyProducts') }}</p>
          <div class="d-flex">
            <app-button app-button invert gradient label="Join Now" width="10rem" class="ma-auto mt-12" @click="joinAsPartner"></app-button>
          </div>
        </v-card-text>
        <v-card-actions class="transparent">
          <v-btn color="transparent mx-auto" depressed fab @click="showJoinNowDialog = false">
            <v-icon x-large class="transparent grey--text text--lighten-2">mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <BecomeAgentDialog v-if="showPartnerDialog === true" v-model="showPartnerDialog" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { localeHelper } from '@/utils'
import VueSlickCarousel from 'vue-slick-carousel'
import LayoutContent from '@/views/layouts/LayoutContent.vue'
import SectionTitle from '@/components/SectionTitle.vue'
import Spacer from '@/components/Spacer.vue'
import ProductReviewSection from '@/components/product/ProductReviewSection.vue'
import ProductImagePreview from '@/components/product/ProductImagePreview.vue'
import ProductDescription from '@/components/product/ProductDescription.vue'
import QuantityBar from '@/components/product/QuantityBar.vue'
import WishlistItemAddedToCartDialog from '@/components/wishlist/WishlistItemAddedToCartDialog.vue'
import FailedAddToCartDialog from '@/components/product/FailedAddToCartDialog.vue'
import ProductMixin from '@/mixins/product.mixin.js'
import AppButton from '@/components/AppButton.vue'
import BecomeAgentDialog from '@/components/storefront/BecomeAgentDialog.vue'
import AgentBanner from '@/components/agent/Banner.vue'
import { uiHelper } from '@/utils'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
// optional style for arrows & dots
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'

export default {
  name: 'detail',
  mixins: [ProductMixin],
  components: {
    LayoutContent,
    SectionTitle,
    Spacer,
    ProductImagePreview,
    ProductDescription,
    QuantityBar,
    WishlistItemAddedToCartDialog,
    FailedAddToCartDialog,
    ProductReviewSection,
    AppButton,
    BecomeAgentDialog,
    AgentBanner,
    VueSlickCarousel
  },
  data() {
    return {
      imageOverlay: false,
      showProductDetails: false,
      productId: this.$route.params.productId,
      showAddedToCartDialog: false,
      showFailedAddToCartDialog: false,
      addedToCartQuantity: 0,
      failedAddToCartMessage: '',
      showJoinNowDialog: false,
      showPartnerDialog: false,
      defaultProduct: null,
      selectedImage: null
    }
  },
  created() {
    this.getProduct(this.productId).then(() => {
      if (this.productData.product.volume_variation === 1) {
        let filterArr = this.productData.product.volume_variations.filter((f) => f.stock_status !== 'Out of Stock')
        this.defaultProduct = ''
        this.productData.product.volume_variations.length === this.productData.product.volume_variations.filter((f) => f.stock_status === 'Out of Stock').length
          ? this.productData.product.volume_variations[0]
          : filterArr[0]
      } else this.defaultProduct = this.productData.product
    })
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    ...mapState('wishlist', ['wishlist']),
    ...mapState('order', ['shoppingCart']),
    label() {
      let text = this.productData.product.product_category
      if (this.productData.product.product_subcategory) text = text + ' / ' + this.productData.product.product_subcategory
      return text
    },
    cartQuantity: {
      get() {
        return this.min > 1 ? this.min : 1
      },
      set() {}
    },
    shoppingCartQuantity() {
      if (this.shoppingCart.length) {
        let selectedProduct
        if (this.productData.product.volume_variation === 0) selectedProduct = this.shoppingCart.filter((item) => item.product_id == this.productId)
        else {
          selectedProduct = this.shoppingCart.filter((item) => item.product_volume_variation_id == this.defaultProduct.id)
        }
        if (selectedProduct.length) {
          return selectedProduct[0].cart_quantity
        }
      }
      return 0
    },
    min() {
      return this.shoppingCartQuantity >= this.defaultProduct.minimum ? 1 : this.defaultProduct.minimum
    },
    max() {
      if (this.productData.product.limit_per_order && this.productData.product.limit_per_order <= this.shoppingCartQuantity) {
        return 0
      }
      return this.productData.product.quantity - this.shoppingCartQuantity
    },
    outOfStock() {
      let status = this.defaultProduct && this.defaultProduct.stock_status == 'Out of Stock'
      if (this.productData.product.volume_variation === 1) {
        if (this.productData.product.volume_variations.length === this.productData.product.volume_variations.filter((f) => f.stock_status == 'Out of Stock').length) status = true
        else status = false
      }
      return status
    },
    subtractStock() {
      return this.productData.product.subtract_stock == 1
    },
    role() {
      return this.currentUser ? this.currentUser.roles : 'Member'
    },
    isDiscounted() {
      return this.defaultProduct.discounted_price != null && this.defaultProduct.discounted_price != ''
    },
    isInWishList() {
      let result
      if (this.productData.product.volume_variation === 0) result = this.wishlist.length > 0 && this.wishlist.findIndex((f) => f.id === this.productData.product.id) > -1
      else {
        result = this.wishlist.length > 0 && this.wishlist.findIndex((f) => f.product_volume_variation_id === this.defaultProduct.id) > -1
      }
      return result
    }
  },
  methods: {
    ...mapActions('wishlist', ['addWishlist', 'deleteWishlist']),
    addToCartHandler(quantity) {
      if (!this.currentUser) {
        this.$root.$app.openLoginDialog()
      } else if (this.role === 'Member' && this.productData.product.product_category_id === 17) {
        this.showJoinNowDialog = true
      } else {
        if (this.defaultProduct.limit_per_order && this.shoppingCartQuantity + quantity > this.defaultProduct.limit_per_order) {
          this.showFailedAddToCartDialog = true
          const quantity = this.defaultProduct.limit_per_order - this.shoppingCartQuantity
          this.failedAddToCartMessage = localeHelper.getMessage('message.remainingCartQuantityToAdd', { quantity })
          return
        }
        if (this.subtractStock && this.shoppingCartQuantity + quantity > this.defaultProduct.quantity) {
          this.showFailedAddToCartDialog = true
          const quantity = this.defaultProduct.quantity
          this.failedAddToCartMessage = localeHelper.getMessage('message.cartStockLimit', { quantity })
          return
        }
        this.showAddedToCartDialog = true
        this.addedToCartQuantity = quantity
        let body = {
          product_id: this.productId,
          quantity: quantity,
          is_checked: 0
        }
        if (this.productData.product.volume_variation === 1) Object.assign(body, { product_volume_variation_id: this.defaultProduct.id })
        let requestBody = { products: [body] }
        this.addToCart(requestBody)
        this.getProduct(this.productId)
      }
    },
    async addToWishlist() {
      let body = { product_id: this.productId }
      if (!this.currentUser) this.$root.$app.openLoginDialog()
      else {
        if (this.productData.product.volume_variation === 1) Object.assign(body, { product_volume_variation_id: this.defaultProduct.id })
        await this.addWishlist(body)
      }
    },
    async removeFromWishlist() {
      let index
      if (this.productData.product.volume_variation === 0) index = this.wishlist.findIndex((f) => f.id === parseInt(this.productId))
      else {
        index = this.wishlist.findIndex((f) => f.product_volume_variation_id === this.defaultProduct.id)
      }
      await this.deleteWishlist([this.wishlist[index].wish_id])
    },
    resetCartQuantity() {
      this.cartQuantity = 1
    },
    joinAsPartner() {
      this.showJoinNowDialog = false
      this.showPartnerDialog = true
    },
    formatPrice(val) {
      return uiHelper.priceFormatter(val)
    },
    showNext() {
      this.$refs.carousel.next()
      let currentIndex = this.productData.product.volume_variations.findIndex((f) => f.id === this.defaultProduct.id)
      let nextIndex = currentIndex + 1
      if (currentIndex > -1 && nextIndex < this.productData.product.volume_variations.length) this.defaultProduct = this.productData.product.volume_variations[currentIndex + 1]
    },
    showPrev() {
      this.$refs.carousel.prev()
      let currentIndex = this.productData.product.volume_variations.findIndex((f) => f.id === this.defaultProduct.id)
      let prevIndex = currentIndex - 1
      if (currentIndex > -1 && prevIndex > -1) this.defaultProduct = this.productData.product.volume_variations[currentIndex - 1]
    },
    setVariation(product) {
      this.defaultProduct = product
    },
    viewImage(url) {
      this.selectedImage = url
      this.imageOverlay = true
    }
  }
}
</script>

<style lang="scss" scoped>
.position-relative {
  position: relative;
}

.discount-circle {
  position: absolute;
  right: 25px;
  top: 25px;
  height: 60px;
  width: 60px;
  font-size: 1.2rem;
  line-height: 1;
}

@media (min-width: 1264px) {
  .discount-circle {
    right: 5%;
    top: 5%;
    width: 100px;
    height: 100px;
    font-size: 2rem;
  }
}

.sold-out-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 180px;
  height: 55px;
  font-size: 1.8rem;

  @media only screen and (min-width: 640px) {
    width: 180px;
    height: 55px;
    font-size: 1.4rem;
  }

  @media only screen and (min-width: 1000px) {
    width: 250px;
    height: 80px;
    font-size: 2.5rem;
  }
}

.number-box {
  width: 64px;
  height: 64px;

  &.number-box,
  &.number-box.lighten-1 {
    border: 1px solid #a7a7a7 !important;
  }
}

.heart-icon {
  --box-height: 64px;
  --button-diameter: 40px;
  margin-bottom: calc((var(--box-height) - var(--button-diameter)) / 2);
}

.preview-image {
  max-height: 60vh;
}

.voucher-details {
  height: 80vh;
}

.left-panel {
  border-radius: 20px;
}

.left-panel .v-image {
  background: white;
  height: 45%;
}

.voucher-img {
  height: 45%;
}

.carousel-container {
  width: 280px;

  @media only screen and (min-width: 640px) {
    width: 320px;
  }

  @media only screen and (min-width: 1000px) {
    width: 400px;
  }

  @media only screen and (min-width: 1200px) {
    width: 500px;
  }

  @media only screen and (min-width: 1400px) {
    width: 600px;
  }
}

.variation-box {
}

.variation-img {
  position: relative;
  width: 85px;
  height: 90px;
  background-position: center center;
  background-repeat: no-repeat;
  object-fit: cover;
  object-fit: scale-down;
  background-size: cover;
  border: 4px solid #e0e0e0 !important;

  @media only screen and (min-width: 640px) {
    width: 95px;
    height: 100px;
  }

  @media only screen and (min-width: 1800px) {
    width: 95px;
    height: 100px;
  }
}

.article {
  box-shadow: inset 0 0 0 200px rgba(0, 0, 0, 0.5);
}
.variation-img:hover {
  cursor: pointer;
  border: 2px solid rgba(116, 206, 0, 0.7);
}
.variation-active {
  border: 4px solid rgba(116, 206, 0, 0.7) !important;
}

.variation-out-of-stock {
  border: 4px solid #c2c2c2 !important;
}

.enlarge-icon {
  position: absolute;
  top: -2%;
  right: 3%;
}

.reposition-navigator {
  ::v-deep .v-carousel__controls {
    position: static;

    .v-carousel__controls__item {
      color: #a7a7a7;
      opacity: 1;
      width: 10px;
      height: 10px;

      .v-icon {
        font-size: 0.6rem !important;
      }

      &.v-btn--active {
        color: var(--v-light-green-base);
      }
    }
  }
}
.p-description::v-deep p {
  display: block;
  width: 100%;
  margin: 0 0 10px;
}

// .carousel-dots {
//   color: #a7a7a7;
// }
</style>
